import {
    Colors,
    Icon,
    Text,
    cardBoxShadow,
    cardTransition,
    notTouchedGlowAnimation,
} from '@amelio/ui-library';
import { useState } from 'react';

import type {
    DraggableMultiContainerItem,
    DraggableMultiContainerItemMetadata,
} from '@amelio/ui-library';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';

interface RankingItemProps {
    item: DraggableMultiContainerItem<{ label: string }>;
    metadata: DraggableMultiContainerItemMetadata;
    hasError: boolean;
    isNextContainer: boolean;
    isRankingContainer: boolean;
}

export const RankingItem = ({
    item,
    metadata,
    hasError,
    isNextContainer,
    isRankingContainer,
}: RankingItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const getBorderColor = () => {
        if (hasError) {
            return Colors.ERROR;
        }
        if (isHovered) {
            return Colors.PRIMARY;
        }
        if (isNextContainer) {
            return Colors.PRIMARY;
        }
        return Colors.BASE_200;
    };

    const getShadowStyle = () => {
        if (isHovered || isNextContainer) {
            return { boxShadow: cardBoxShadow };
        }
        return {};
    };

    const getInteractiveColor = () => {
        if (isHovered) {
            return Colors.PRIMARY;
        }
        return Colors.WHITE;
    };

    const getNumberTextColor = () => {
        if (isHovered || metadata.isDragging) {
            return Colors.PRIMARY;
        }
        return Colors.WHITE;
    };

    const getNumberGlow = () => {
        if (isHovered || metadata.isDragging) {
            return {
                borderColor: Colors.PRIMARY,
                boxShadow: `0 0 10px 2px ${Colors.PRIMARY}`,
            };
        }
        return {
            borderColor: Colors.WHITE,
            boxShadow: 'none',
        };
    };

    const shouldShowBurger = () => {
        if (isNextContainer) {
            return true;
        }
        if (isRankingContainer && isHovered) {
            return true;
        }
        return false;
    };

    const getDragStateClasses = () => {
        if (!metadata.isDragging) {
            return 'hover:scale-[1.02] hover:cursor-pointer hover:opacity-80';
        }
        if (isNextContainer) {
            return 'invisible h-0 m-0 overflow-hidden border-0 p-0';
        }
        return 'opacity-50';
    };

    return (
        <div
            className={`relative flex cursor-grab items-center gap-2 rounded-lg border bg-transparent p-4 active:cursor-grabbing md:gap-3 md:p-6 ${cardTransition} ${getDragStateClasses()}`}
            style={{
                borderColor: getBorderColor(),
                ...getShadowStyle(),
                ...(isNextContainer
                    ? {
                          animation: notTouchedGlowAnimation,
                      }
                    : {}),
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={`shrink-0 transition-opacity duration-300 ${shouldShowBurger() ? 'opacity-100' : 'opacity-0'}`}
            >
                <Icon
                    name={'essential-menu'}
                    size={'md'}
                    color={getInteractiveColor()}
                />
            </div>

            {isRankingContainer && (
                <div
                    className={
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 md:h-8 md:w-8'
                    }
                    style={{
                        ...getNumberGlow(),
                        backgroundColor: 'transparent',
                    }}
                >
                    <span
                        className={'text-sm font-semibold'}
                        style={{
                            color: getNumberTextColor(),
                            textShadow:
                                isHovered || metadata.isDragging
                                    ? `0 0 8px ${Colors.PRIMARY}`
                                    : 'none',
                        }}
                    >
                        {metadata.index + 1}
                    </span>
                </div>
            )}

            <Text
                className={'flex-1 text-sm md:text-base'}
                color={getInteractiveColor()}
            >
                <QuestionTextRenderer text={item.data.label} />
            </Text>
        </div>
    );
};
